/**
 * Plan-Based Feature Gating Configuration (Story 2.2)
 *
 * Single source of truth for ALL plan limits across the entire app (server + web).
 *
 * CRITICAL: This is the ONLY location for plan limits in the entire codebase.
 * - Server code: import from "@provenleap/types"
 * - Client code: import from "@provenleap/types"
 * - DO NOT create derived configs or hardcode limit values elsewhere
 *
 * Adding a new feature limit:
 * 1. Add property to PlanLimits interface
 * 2. Add value to ALL four plans (TypeScript will error if any is missing)
 * 3. Update tests in plans.test.ts
 * 4. Use requireFeature() or checkFeatureAccess() in your story
 */

/**
 * Subscription plan tier.
 *
 * IMPORTANT: This type is intentionally duplicated from @provenleap/db to avoid
 * a circular dependency (@provenleap/types cannot depend on @provenleap/db).
 * A runtime test in plans.test.ts validates that both definitions stay in sync.
 * If you add/remove a plan tier, update BOTH:
 * - packages/types/src/plans.ts (this file)
 * - packages/db/src/schema/auth.ts
 */
export type Plan = "free" | "starter" | "pro" | "agency";

/** Ordered plan hierarchy for upgrade comparisons. */
export const PLAN_HIERARCHY: Plan[] = ["free", "starter", "pro", "agency"];

export interface PlanLimits {
  clientLimit: number;
  // ALL tiers MUST have 'all' — no gating per AC 2.9#3. DO NOT change.
  schedulingFrequency: "all";
  /** Max team members per tenant. null = unlimited (paid tiers). Free tier = 1. */
  teamMembers: number | null;
  pdfBranding: boolean;
  prioritySupport: boolean;
  restApiAccess: boolean;
  customDomain: boolean;
  /**
   * Marketing/pricing-table flag AND the future Epic 27/28 alerts/tasks
   * enforcement key — NOT the goals creation gate (Story 18.10).
   *
   * goalsAlertsTasks (free=false) predates Epic 18 and is consumed ONLY by
   * display surfaces (PricingSection.tsx, billing.tsx, PlanLimitsCard boolean
   * badges). It must NEVER gate the goals routes: requireFeature(
   * "goalsAlertsTasks") would 403 every Free request and nullify the epic's
   * "2 goals taste" strategy. Goals are numerically gated via `goalLimit`
   * (below). When Epic 27/28 land alerts & tasks, those features enforce
   * against THIS boolean.
   */
  goalsAlertsTasks: boolean;
  /**
   * Max goals (Story 18.10). free=2, starter=10, pro=25, agency=null (unlimited).
   *
   * 🔴 DUAL SCOPE — the limit NUMBER lives here, the SCOPE rule lives in the
   * count resolver (getGoalCount, apps/server/src/services/plan-limits.ts):
   *   free      = 2 goals TOTAL across the tenant (tenant-wide count)
   *   starter   = 10 PER CLIENT
   *   pro       = 25 PER CLIENT
   *   agency    = unlimited (safety-capped at SAFETY_CAPS.goalLimit)
   * Demo-client goals NEVER count toward the limit (the getClientCount
   * demo_mode=0 precedent). Goal STATUS is irrelevant — a complete/paused goal
   * still occupies a slot; deleting frees it.
   */
  goalLimit: number | null;
  /**
   * Max metric alerts (Story 27.4). free=2, starter=5, pro=15, agency=null (unlimited).
   *
   * 🔴 DUAL SCOPE — the limit NUMBER lives here, the SCOPE rule lives in the
   * count resolver (getAlertCount, apps/server/src/services/plan-limits.ts):
   *   free      = 2 alerts TOTAL across the tenant (tenant-wide count)
   *   starter   = 5 PER CLIENT
   *   pro       = 15 PER CLIENT
   *   agency    = unlimited (safety-capped at SAFETY_CAPS.alertLimit)
   * Demo-client alerts NEVER count toward the limit (the getClientCount/
   * getGoalCount demo_mode=0 precedent). Alert `is_active` status is irrelevant —
   * an inactive (disarmed) alert still occupies a slot; deleting frees it.
   *
   * NOT gated by the `goalsAlertsTasks` boolean above: a hard feature-flag block
   * would 403 every Free alerts request and nullify the "2-alert taste" upgrade
   * strategy (the same lesson `goalLimit` learned). Enforcement is numeric:
   * `count < alertLimit` via requireFeature (the goals precedent).
   */
  alertLimit: number | null;
  /**
   * Max tasks (Story 28.4). free=5, starter=50, pro=null, agency=null (unlimited).
   *
   * 🔴 TENANT-WIDE on ALL tiers — UNLIKE goalLimit/alertLimit (tenant-wide on
   * free but PER-CLIENT on paid). The count resolver (getTaskCount,
   * apps/server/src/services/plan-limits.ts) takes NO clientId and NO plan
   * branch: free=5 and starter=50 are both tenant-wide totals; pro & agency are
   * unlimited (both rely on SAFETY_CAPS.taskLimit=10000 — Pro is null here, so
   * it routes through the null/safety-cap branch, not the numeric branch).
   * Demo-client tasks NEVER count (the getClientCount demo_mode=0 precedent),
   * and client-less (general) tasks DO count (getTaskCount LEFT-JOINs clients
   * tolerating NULL client_id — tasks.client_id is nullable per Story 28.1).
   *
   * NOT gated by the `goalsAlertsTasks` boolean above (a requireFeature on it
   * would 403 every Free request and kill the 5-task-taste funnel — the
   * goalLimit/alertLimit lesson). Enforcement is numeric: count < taskLimit.
   */
  taskLimit: number | null;
  /** Max integrations per client (Story 4.5). null = unlimited (up to safety cap). */
  integrationsPerClient: number | null;
  /** Max custom (cloned) report templates per tenant (Story 5.2). null = unlimited. */
  customTemplateLimit: number | null;
  /** Max active (non-revoked) client portal share links per tenant (Story 14.2). null = unlimited (up to safety cap). */
  portalLinkLimit: number | null;
}

/** Fixed history window for all plans (Story 2.10). */
export const HISTORY_MONTHS = 24;

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: {
    clientLimit: 1,
    schedulingFrequency: "all",
    teamMembers: 1,
    pdfBranding: false,
    prioritySupport: false,
    restApiAccess: false,
    customDomain: false,
    goalsAlertsTasks: false,
    goalLimit: 2,
    alertLimit: 2,
    taskLimit: 5,
    integrationsPerClient: null,
    customTemplateLimit: 0,
    portalLinkLimit: 3,
  },
  starter: {
    clientLimit: 5,
    schedulingFrequency: "all",
    teamMembers: null,
    pdfBranding: true,
    prioritySupport: false,
    restApiAccess: false,
    customDomain: true,
    goalsAlertsTasks: true,
    goalLimit: 10,
    alertLimit: 5,
    taskLimit: 50,
    integrationsPerClient: null,
    customTemplateLimit: 5,
    portalLinkLimit: 25,
  },
  pro: {
    clientLimit: 20,
    schedulingFrequency: "all",
    teamMembers: null,
    pdfBranding: true,
    prioritySupport: false,
    restApiAccess: true,
    customDomain: true,
    goalsAlertsTasks: true,
    goalLimit: 25,
    alertLimit: 15,
    taskLimit: null,
    integrationsPerClient: null,
    customTemplateLimit: 20,
    portalLinkLimit: 100,
  },
  agency: {
    clientLimit: 50,
    schedulingFrequency: "all",
    teamMembers: null,
    pdfBranding: true,
    prioritySupport: true,
    restApiAccess: true,
    customDomain: true,
    goalsAlertsTasks: true,
    goalLimit: null,
    alertLimit: null,
    taskLimit: null,
    integrationsPerClient: null,
    customTemplateLimit: null,
    portalLinkLimit: null,
  },
};

export type PlanFeature = keyof PlanLimits;

/** All feature keys for runtime validation. */
export const PLAN_FEATURE_KEYS: PlanFeature[] = Object.keys(
  PLAN_LIMITS.free
) as PlanFeature[];

/** Internal safety caps for "unlimited" (null) features to prevent abuse. */
export const SAFETY_CAPS: Partial<Record<PlanFeature, number>> = {
  integrationsPerClient: 50,
  customTemplateLimit: 100,
  portalLinkLimit: 1000,
  // goalLimit: invisible cap on the unlimited (agency) tier. NOT a sellable
  // tier — this is runaway-script protection (a buggy bulk-create loop can't
  // mint 100k goals against a single client and degrade the demo). House style
  // per teamMembers/portalLinkLimit.
  goalLimit: 500,
  // alertLimit: invisible cap on the unlimited (agency) tier. NOT a sellable
  // tier — runaway-script protection (a buggy bulk-create loop can't mint 100k
  // alerts against a single client and degrade the demo / the 27.3 eval cron).
  // House style per goalLimit/teamMembers/portalLinkLimit.
  alertLimit: 500,
  // taskLimit: invisible cap on the unlimited (pro + agency) tiers. Set HIGH
  // (10000) because the task count is TENANT-WIDE (not per-client like
  // goal/alert) AND tasks accumulate via 28.3 recurrence (each completion mints
  // a new row). Pure runaway-loop protection — must never fire for human-paced
  // use. BOTH pro and agency are null here, so both consult this cap.
  taskLimit: 10000,
  // teamMembers: invisible cap to prevent reseller-style abuse on paid tiers.
  // 200 covers >99% of legitimate agencies (largest agencies in the wild ~150 staff).
  // Above this threshold, surface a "Contact us for enterprise pricing" prompt.
  teamMembers: 200,
};

/**
 * Epic 39 — invisible per-tenant ceiling on BILLABLE clients under per-client
 * overage. Once a paid tenant's clientLimit becomes an included count (clients
 * allowed above it, billed as overage), this is the circuit breaker: a runaway
 * bulk-create loop can never mint unbounded billable clients / unbounded charge.
 * NOT a sellable wall — pure runaway-loop protection, house style mirroring
 * SAFETY_CAPS.taskLimit=10000. Hit → ClientCeilingReachedError (contact support),
 * never an auto-bill. Free stays hard-capped at PLAN_LIMITS.free.clientLimit=1.
 */
export const BILLABLE_CLIENT_CEILING = 10_000;

/** Human-readable display names for plan features. Used in upgrade modals and limit cards. */
export const FEATURE_DISPLAY_NAMES: Record<PlanFeature, string> = {
  clientLimit: "client",
  schedulingFrequency: "scheduling frequency",
  teamMembers: "team member",
  pdfBranding: "branding",
  prioritySupport: "priority support",
  restApiAccess: "REST API access",
  customDomain: "custom domain",
  goalsAlertsTasks: "goals, alerts & tasks",
  // Singular — the UpgradeLimitModal pluralizes ("…for up to 10 goals").
  goalLimit: "goal",
  // Singular — the UpgradeLimitModal pluralizes ("…for up to 15 alerts").
  alertLimit: "alert",
  // Singular — the UpgradeLimitModal pluralizes ("…for up to 50 tasks").
  taskLimit: "task",
  integrationsPerClient: "integration per client",
  customTemplateLimit: "custom report template",
  portalLinkLimit: "share link",
};

// ============================================================================
// Helper Functions
// ============================================================================

/** Type guard: returns true if limit is null (unlimited). */
export function isUnlimited(limit: number | null): limit is null {
  return limit === null;
}

/** Returns true if current count has reached or exceeded the limit. Unlimited (null) is never at limit. */
export function isAtLimit(current: number, limit: number | null): boolean {
  if (limit === null) return false;
  return current >= limit;
}

/** Formats a limit value for display. Null becomes "Unlimited". */
export function formatLimit(limit: number | null): string {
  return limit === null ? "Unlimited" : String(limit);
}

/**
 * Formats a plan feature value for display in pricing/billing UIs.
 *
 * - pdfBranding boolean: false → fallbackBrandingLabel (default "ProvenLeap"), true → "Your brand"
 * - Other booleans: false → "—", true → "Yes"
 * - null → "Unlimited"
 * - number → stringified
 */
export function formatFeatureValue(
  key: PlanFeature,
  value: PlanLimits[PlanFeature],
  fallbackBrandingLabel = "ProvenLeap"
): string {
  if (key === "pdfBranding") return value ? "Your brand" : fallbackBrandingLabel;
  if (typeof value === "boolean") return value ? "Yes" : "—";
  if (value === null) return "Unlimited";
  return String(value);
}

/**
 * Returns the minimum (lowest-tier) plan that satisfies a given feature requirement.
 *
 * For numeric features: returns the lowest plan where limit >= requiredValue (or null/unlimited).
 * For boolean features: returns the lowest plan where the value matches.
 * For string features: returns the lowest plan where the value matches.
 */
export function getMinimumPlanForFeature(
  feature: PlanFeature,
  requiredValue: number | boolean | string
): Plan {
  for (const plan of PLAN_HIERARCHY) {
    const limit = PLAN_LIMITS[plan][feature];
    if (typeof requiredValue === "boolean" && limit === requiredValue)
      return plan;
    if (typeof requiredValue === "string" && limit === requiredValue)
      return plan;
    if (
      typeof requiredValue === "number" &&
      typeof limit === "number" &&
      limit >= requiredValue
    )
      return plan;
    if (typeof requiredValue === "number" && limit === null) return plan; // unlimited satisfies any count
  }
  return "agency";
}

// ============================================================================
// Pricing (display only — single source of truth for all UI)
// ============================================================================

/**
 * Plan pricing in dollars (display only).
 * Source of truth: apps/server/src/config/stripe-plans.ts → PLAN_INFO (in cents).
 * Keep in sync: if prices change, update BOTH this file and PLAN_INFO.
 * effectiveMonthly is pre-computed to avoid floating-point display issues.
 */
export const PLAN_PRICING: Record<
  Exclude<Plan, "free">,
  { monthly: number; annual: number; effectiveMonthly: string }
> = {
  starter: { monthly: 49, annual: 490, effectiveMonthly: "40.83" },
  pro: { monthly: 149, annual: 1490, effectiveMonthly: "124.17" },
  agency: { monthly: 299, annual: 2990, effectiveMonthly: "249.17" },
};

/**
 * Epic 39 — per-client overage rates in DOLLARS (advisory/display only).
 * The AUTHORITATIVE charge is ALWAYS Stripe's line items (F3); these drive the
 * crossover NUDGE math (39-5) only, which must never be co-located with a
 * Stripe-derived figure. Source of truth for cents: GRADUATED_TIERS
 * (apps/server/src/config/stripe-plans.ts). Annual = monthly × 10.
 */
export const OVERAGE_RATES: Record<
  Exclude<Plan, "free">,
  { monthly: number; annual: number }
> = {
  starter: { monthly: 10, annual: 100 },
  pro: { monthly: 7.5, annual: 75 },
  agency: { monthly: 6, annual: 60 },
};

/** Epic 39.5 — advisory crossover nudge result (never the authoritative charge). */
export interface CrossoverAdvice {
  inOverage: boolean;
  overageCount: number;
  nextTier: Exclude<Plan, "free"> | null;
  /** Smallest client count where the next tier is at least as cheap (or null). */
  crossoverCount: number | null;
  /** True once the next tier is at least as cheap → show a savings-framed nudge. */
  shouldNudge: boolean;
  /** Advisory monthly $ saved by upgrading at the current count (>= 0). */
  monthlySavings: number;
}

/**
 * Epic 39.5 — ADVISORY crossover math. At the current client count, is the next
 * tier at least as cheap? This is a NUDGE hint sourced from the rate table, NOT
 * the authoritative Stripe charge — never render it beside a Stripe-derived
 * figure. Uses monthly rates (annual scales ×10 on both sides, so the crossover
 * count is interval-independent).
 */
export function computeCrossover(
  plan: Plan,
  clientCount: number
): CrossoverAdvice {
  const none: CrossoverAdvice = {
    inOverage: false,
    overageCount: 0,
    nextTier: null,
    crossoverCount: null,
    shouldNudge: false,
    monthlySavings: 0,
  };
  if (plan === "free") return none;

  const order: Exclude<Plan, "free">[] = ["starter", "pro", "agency"];
  const idx = order.indexOf(plan);
  const included = PLAN_LIMITS[plan].clientLimit;
  const overageCount = Math.max(0, clientCount - included);
  const inOverage = overageCount > 0;
  const nextTier = idx < order.length - 1 ? order[idx + 1] : null;
  if (!nextTier) return { ...none, inOverage, overageCount };

  const monthlyFor = (p: Exclude<Plan, "free">, count: number): number =>
    PLAN_PRICING[p].monthly +
    Math.max(0, count - PLAN_LIMITS[p].clientLimit) * OVERAGE_RATES[p].monthly;

  const thisMonthly = monthlyFor(plan, clientCount);
  const nextMonthly = monthlyFor(nextTier, clientCount);
  const nextIncluded = PLAN_LIMITS[nextTier].clientLimit;

  let crossoverCount: number | null = null;
  for (let c = included + 1; c <= nextIncluded + 500; c++) {
    if (monthlyFor(nextTier, c) <= monthlyFor(plan, c)) {
      crossoverCount = c;
      break;
    }
  }

  const shouldNudge = inOverage && nextMonthly <= thisMonthly;
  const monthlySavings = shouldNudge
    ? Math.max(0, Math.round((thisMonthly - nextMonthly) * 100) / 100)
    : 0;

  return { inOverage, overageCount, nextTier, crossoverCount, shouldNudge, monthlySavings };
}

/**
 * Plan display names (single source of truth for UI labels).
 * Use these instead of hardcoded strings. Database/types retain original
 * plan keys ("free", "starter") for backward compatibility; only display
 * labels changed with PUV2 pricing update (free→"Solo", starter→"Team").
 */
export const PLAN_DISPLAY_NAMES: Record<Plan, string> = {
  free: "Solo",
  starter: "Team",
  pro: "Pro",
  agency: "Agency",
};

// ============================================================================
// Powered-by-ProvenLeap Footer Rule (Story 14.5 — moved from apps/server/src/services/portal-links.ts)
// ============================================================================

/**
 * Determines whether the client portal renders the "Powered by ProvenLeap"
 * footer link. ONLY the free (Solo) tier shows it — every paid tier is
 * fully white-labeled (PUV2.4 white-label consistency commit).
 *
 * Plan-derived at render time, NOT persisted in `tenants.branding`. This means
 * a downgrade from paid → free immediately restores the footer on the next
 * portal load (no migration, no batch job). Likewise an upgrade removes it
 * instantly.
 *
 * Single source of truth: imported by both the server (portal-view meta/view
 * routes) and the web (PortalBrandingPreview in the settings UI) so the
 * preview shows exactly what the client sees.
 */
export function computePoweredByProvenLeap(plan: Plan): boolean {
  return plan === "free";
}
